import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';

interface CategoryPromotion {
    id: number;
    name: string;
    promotion_type: string;
    value: number;
    starts_at: string | null;
    ends_at: string | null;
    category_name: string;
}

interface Category {
    id: number;
    name: string;
    code: string;
    parent_id?: number | null;
    translations?: {
        HU?: string[];
    };
}

interface CategoryPromotionManagerProps {
    shopId: number;
    sliceId?: number;
    categoryId?: number;
    onRefresh?: () => void;
}

const CategoryPromotionManager: React.FC<CategoryPromotionManagerProps> = ({
    shopId,
    sliceId,
    categoryId: initialCategoryId,
    onRefresh
}) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(initialCategoryId || null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [promotions, setPromotions] = useState<CategoryPromotion[]>([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        promotion_type: 'percent',
        value: '',
        starts_at: '',
        ends_at: '',
    });
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Build category hierarchy display
    const buildCategoryLabel = (cat: Category, allCats: Category[]): string => {
        const getDisplayName = (category: Category): string => {
            // Use HU translation if available, otherwise use name, otherwise use code
            if (category.translations?.HU && category.translations.HU.length > 0) {
                return category.translations.HU[0];
            }
            return category.name || category.code;
        };

        const path: string[] = [getDisplayName(cat)];
        let current = cat;

        while (current.parent_id) {
            const parent = allCats.find(c => c.id === current.parent_id);
            if (parent) {
                path.unshift(getDisplayName(parent));
                current = parent;
            } else {
                break;
            }
        }

        return path.join(' > ');
    };

    // Filter categories by search
    const filteredCategories = categories.filter(cat => {
        const label = buildCategoryLabel(cat, categories).toLowerCase();
        return label.includes(searchQuery.toLowerCase());
    }).sort((a, b) => buildCategoryLabel(a, categories).localeCompare(buildCategoryLabel(b, categories)));

    // Fetch categories
    const fetchCategories = async () => {
        setCategoriesLoading(true);
        try {
            const res = await fetch('/api/admin/categories?page=1&per_page=999');
            const data = await res.json();
            if (Array.isArray(data.categories)) {
                setCategories(data.categories);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        } finally {
            setCategoriesLoading(false);
        }
    };

    useEffect(() => {
        if (!initialCategoryId) {
            fetchCategories();
        }
    }, [initialCategoryId]);

    // Fetch promotions
    const fetchPromotions = async () => {
        if (!selectedCategoryId) return;

        setLoading(true);
        setError(null);
        try {
            const res = await fetch(
                `/api/admin/category-promotions?shop_id=${shopId}&category_id=${selectedCategoryId}`
            );
            const data = await res.json();

            if (data.success && Array.isArray(data.promotions)) {
                setPromotions(data.promotions);
            } else {
                setPromotions([]);
            }
        } catch (err) {
            console.error('Error fetching promotions:', err);
            setError('Nem sikerült az akciók betöltése');
            setPromotions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (shopId && selectedCategoryId) {
            fetchPromotions();
        }
    }, [shopId, selectedCategoryId]);
    const handleAddPromotion = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!formData.name || !formData.value || !selectedCategoryId) {
            setError('Tölts ki minden mezőt és válassz kategóriát!');
            return;
        }

        try {
            const res = await fetch('/api/admin/category-promotions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    shop_id: shopId,
                    slice_id: sliceId || null,
                    category_id: selectedCategoryId,
                    name: formData.name,
                    promotion_type: formData.promotion_type,
                    value: parseFloat(formData.value),
                    starts_at: formData.starts_at ? new Date(formData.starts_at).toISOString() : null,
                    ends_at: formData.ends_at ? new Date(formData.ends_at).toISOString() : null,
                    stackable: true,
                    apply_to_variants: true,
                }),
            });

            const data = await res.json();

            if (data.success) {
                setSuccess('✅ Akció sikeresen hozzáadva!');
                setFormData({
                    name: '',
                    promotion_type: 'percent',
                    value: '',
                    starts_at: '',
                    ends_at: '',
                });
                setShowForm(false);
                await fetchPromotions();
                onRefresh?.();
            } else {
                setError(data.error || 'Hiba az akció mentésekor');
            }
        } catch (err) {
            console.error('Error adding promotion:', err);
            setError('Hiba az akció mentésekor');
        }
    };

    const handleDeletePromotion = async (id: number) => {
        if (!window.confirm('Biztosan törlöd ezt az akciót?')) {
            return;
        }

        try {
            const res = await fetch(`/api/admin/category-promotions/${id}`, {
                method: 'DELETE',
            });

            const data = await res.json();

            if (data.success) {
                setSuccess('✅ Akció sikeresen törölve!');
                await fetchPromotions();
                onRefresh?.();
            } else {
                setError('Hiba az akció törlésekor');
            }
        } catch (err) {
            console.error('Error deleting promotion:', err);
            setError('Hiba az akció törlésekor');
        }
    };

    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '8px',
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(34, 197, 94, 0.2)',
        borderRadius: '4px',
        color: '#ddd',
        fontSize: '12px',
    };

    const buttonStyle: React.CSSProperties = {
        padding: '6px 12px',
        backgroundColor: 'rgba(34, 197, 94, 0.2)',
        color: '#22c55e',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 'bold',
        transition: 'all 0.2s',
    };

    const deleteButtonStyle: React.CSSProperties = {
        padding: '6px 10px',
        backgroundColor: 'rgba(239, 68, 68, 0.15)',
        color: '#ef4444',
        border: '1px solid rgba(239, 68, 68, 0.4)',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        transition: 'all 0.2s',
        flexShrink: 0,
        minWidth: 'fit-content',
    };

    if (loading) {
        return <div style={{ color: '#999', fontSize: '12px' }}>⏳ Betöltés...</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'auto', maxHeight: '100%', padding: '4px 0' }}>
            {/* Category Selector (if no categoryId provided) */}
            {!initialCategoryId && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px', width: '100%' }}>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#999', textTransform: 'uppercase', lineHeight: '1.4' }}>
                        📁 Kategória kiválasztása
                    </label>

                    {/* Search input */}
                    <input
                        type="text"
                        placeholder="Keresés kategóriában..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '4px',
                            color: '#ddd',
                            fontSize: '12px',
                            lineHeight: '1.4',
                            boxSizing: 'border-box',
                        }}
                    />

                    {/* Category dropdown */}
                    <select
                        value={selectedCategoryId || ''}
                        onChange={(e) => setSelectedCategoryId(e.target.value ? Number(e.target.value) : null)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid rgba(34, 197, 94, 0.2)',
                            borderRadius: '4px',
                            color: selectedCategoryId ? '#ddd' : '#666',
                            fontSize: '12px',
                            lineHeight: '1.4',
                            boxSizing: 'border-box',
                        }}
                        disabled={categoriesLoading}
                    >
                        <option value="">Válassz egy kategóriát...</option>
                        {filteredCategories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                                {buildCategoryLabel(cat, categories)}
                            </option>
                        ))}
                    </select>

                    {selectedCategoryId && (
                        <div style={{ fontSize: '11px', color: '#22c55e', lineHeight: '1.4', marginTop: '4px' }}>
                            ✅ Kiválasztva: {buildCategoryLabel(categories.find(c => c.id === selectedCategoryId)!, categories)}
                        </div>
                    )}
                </div>
            )}

            {/* Messages */}
            {error && (
                <div
                    style={{
                        padding: '10px',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '4px',
                        color: '#ef4444',
                        fontSize: '12px',
                        lineHeight: '1.4',
                        marginTop: '4px',
                    }}
                >
                    {error}
                </div>
            )}

            {success && (
                <div
                    style={{
                        padding: '10px',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: '4px',
                        color: '#22c55e',
                        fontSize: '12px',
                        lineHeight: '1.4',
                        marginTop: '4px',
                    }}
                >
                    {success}
                </div>
            )}

            {/* Promotions List */}
            {selectedCategoryId && promotions.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {promotions.map((promo) => (
                        <div
                            key={promo.id}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                padding: '10px 12px',
                                backgroundColor: 'rgba(34, 197, 94, 0.08)',
                                border: '1px solid rgba(34, 197, 94, 0.2)',
                                borderRadius: '4px',
                                gap: '12px',
                                minHeight: '60px',
                            }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 'bold', fontSize: '12px', color: '#ddd', marginBottom: '4px' }}>
                                    {promo.name}
                                </div>
                                <div style={{ fontSize: '11px', color: '#999', lineHeight: '1.4' }}>
                                    {promo.promotion_type === 'percent' ? `${promo.value}%` : `${promo.value}€`}
                                    {promo.starts_at && ` • ${new Date(promo.starts_at).toLocaleDateString('hu-HU')}`}
                                    {promo.ends_at && ` - ${new Date(promo.ends_at).toLocaleDateString('hu-HU')}`}
                                </div>
                            </div>
                            <button
                                onClick={() => handleDeletePromotion(promo.id)}
                                style={deleteButtonStyle}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239, 68, 68, 0.25)';
                                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239, 68, 68, 0.6)';
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(239, 68, 68, 0.15)';
                                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239, 68, 68, 0.4)';
                                }}
                                title="Törlés"
                            >
                                <Trash2 size={14} />
                                <span>Törlés</span>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Button */}
            {selectedCategoryId && !showForm && (
                <button
                    onClick={() => setShowForm(true)}
                    style={buttonStyle}
                    onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(34, 197, 94, 0.35)';
                    }}
                    onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(34, 197, 94, 0.2)';
                    }}
                >
                    + Új Akció
                </button>
            )}

            {/* Form */}
            {showForm && (
                <form onSubmit={handleAddPromotion} style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px', backgroundColor: 'rgba(34, 197, 94, 0.05)', borderRadius: '4px', border: '1px solid rgba(34, 197, 94, 0.15)' }}>
                    <input
                        type="text"
                        placeholder="Akció neve (pl: Friss húsok -25%)"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        style={inputStyle}
                    />

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <select
                            value={formData.promotion_type}
                            onChange={(e) => setFormData({ ...formData, promotion_type: e.target.value })}
                            style={inputStyle}
                        >
                            <option value="percent">Százalék (%)</option>
                            <option value="fixed">Fix ár (€)</option>
                        </select>

                        <input
                            type="number"
                            placeholder="Érték"
                            step="0.01"
                            min="0"
                            value={formData.value}
                            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                            style={inputStyle}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <input
                            type="datetime-local"
                            value={formData.starts_at}
                            onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                            style={inputStyle}
                            title="Kezdete"
                        />

                        <input
                            type="datetime-local"
                            value={formData.ends_at}
                            onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                            style={inputStyle}
                            title="Vége"
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            type="submit"
                            style={{
                                flex: 1,
                                ...buttonStyle,
                            }}
                            onMouseEnter={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(34, 197, 94, 0.35)';
                            }}
                            onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(34, 197, 94, 0.2)';
                            }}
                        >
                            ✓ Mentés
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowForm(false)}
                            style={{
                                flex: 1,
                                padding: '6px 12px',
                                backgroundColor: 'rgba(107, 114, 128, 0.2)',
                                color: '#999',
                                border: '1px solid rgba(107, 114, 128, 0.3)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                            }}
                        >
                            ✕ Mégse
                        </button>
                    </div>
                </form>
            )}

            {selectedCategoryId && promotions.length === 0 && !showForm && (
                <div style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                    Nincsenek kategória-akciók ehhez a bolthoz és kategóriához
                </div>
            )}
        </div>
    );
};

export default CategoryPromotionManager;
